import { lazy, Suspense } from 'react'
import type { ComponentType } from 'react'

/**
 * Wraps React.lazy() with one silent retry on chunk load failure.
 * Covers transient CDN / network hiccups that trigger ChunkLoadError and
 * land the user on the "Algo deu errado" error boundary.
 */
function lazyRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy<T>(() =>
    factory().catch(
      () => new Promise<{ default: T }>(resolve => setTimeout(resolve, 400)).then(factory),
    ),
  )
}
import { Analytics } from '@vercel/analytics/react'
import { SpeedInsights } from '@vercel/speed-insights/react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { ThemeProvider } from './contexts/ThemeContext'
import { LangProvider } from './contexts/LangContext'
import { AuthProvider } from './contexts/AuthContext'
import { OfficeProvider } from './contexts/OfficeContext'
import { ProtectedRoute } from './components/ProtectedRoute'
import { UserPrefsSync } from './components/UserPrefsSync'
import { AcquisitionSync } from './components/AcquisitionSync'
import { SuperAdminRoute } from './components/SuperAdminRoute'
import { AppLayout } from './components/AppLayout'
import { ErrorBoundary } from './components/ErrorBoundary'
import { RouteSeo } from './components/RouteSeo'
import Landing from './pages/Landing'
import Login from './pages/Login'

// Landing, Login and AppLayout stay in the first bundle (the doors most visits
// come through, and AppLayout would add a second round trip before every panel
// page). Everything else loads on demand, so the landing downloads less JS.
const ForgotPassword = lazyRetry(() => import('./pages/ForgotPassword'))
const ResetPassword = lazyRetry(() => import('./pages/ResetPassword'))
const AcceptInvite = lazyRetry(() => import('./pages/AcceptInvite'))
const Legal = lazyRetry(() => import('./pages/Legal'))
const FAQ = lazyRetry(() => import('./pages/FAQ'))
const Support = lazyRetry(() => import('./pages/Support'))
const About = lazyRetry(() => import('./pages/About'))
const AdminLayout = lazyRetry(() => import('./components/admin/AdminLayout').then((m) => ({ default: m.AdminLayout })))
const Onboarding = lazyRetry(() => import('./pages/Onboarding'))
const SignupQuick = lazyRetry(() => import('./pages/SignupQuick'))
const Overview = lazyRetry(() => import('./pages/Overview'))
const Clients = lazyRetry(() => import('./pages/Clients'))
const ClientDetail = lazyRetry(() => import('./pages/ClientDetail'))
const Cases = lazyRetry(() => import('./pages/Cases'))
const CaseDetail = lazyRetry(() => import('./pages/CaseDetail'))
const Alerts = lazyRetry(() => import('./pages/Alerts'))
const Reports = lazyRetry(() => import('./pages/Reports'))
const AuditLog = lazyRetry(() => import('./pages/AuditLog'))
const Settings = lazyRetry(() => import('./pages/Settings'))
const Plans = lazyRetry(() => import('./pages/Plans'))
const SentryTest      = lazyRetry(() => import('./pages/SentryTest'))
const AdminOverview   = lazyRetry(() => import('./pages/admin/AdminOverview'))
const AdminOffices    = lazyRetry(() => import('./pages/admin/AdminOffices'))
const AdminTrials     = lazyRetry(() => import('./pages/admin/AdminTrials'))
const AdminFinanceiro = lazyRetry(() => import('./pages/admin/AdminFinanceiro'))
const AdminHealth     = lazyRetry(() => import('./pages/admin/AdminHealth'))
const AdminAnalytics  = lazyRetry(() => import('./pages/admin/AdminAnalytics'))
const AdminAcquisition = lazyRetry(() => import('./pages/admin/AdminAcquisition'))

export default function App() {
  return (
    <LangProvider>
    <ThemeProvider>
    <BrowserRouter>
      <AuthProvider>
      <UserPrefsSync />
      <AcquisitionSync />
      <RouteSeo />
      <OfficeProvider>
        <ErrorBoundary>
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-page)' }}>
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-[var(--brand)]" />
          </div>
        }>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/signup" element={<Navigate to="/onboarding" replace />} />
          {/* /signup-quick — paywall-gated 3-field signup that hands off to
              Stripe before the full onboarding wizard. Driven by pricing CTAs
              on Landing/Plans. See src/pages/SignupQuick.tsx. */}
          <Route path="/signup-quick" element={<SignupQuick />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/legal" element={<Legal />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/support" element={<Support />} />
          <Route path="/about" element={<About />} />
          {/* Plans — public by design. Anonymous visitors can compare plans and
              initiate signup with a plan pre-selected. Logged-in users get the
              in-app checkout/portal flow. Kept outside AppLayout so the page
              renders cleanly for not-yet-signed-up visitors. */}
          <Route path="/plans" element={<Plans />} />
          {/* Hidden Sentry smoke-test page — not linked anywhere. See pages/SentryTest.tsx. */}
          <Route path="/__sentry-test" element={<SentryTest />} />
          {/* Admin — founder only, protected by SuperAdminRoute (sessionStorage JWT) */}
          <Route path="/admin" element={<SuperAdminRoute><AdminLayout /></SuperAdminRoute>}>
            <Route index element={<AdminOverview />} />
            <Route path="offices" element={<AdminOffices />} />
            <Route path="trials" element={<AdminTrials />} />
            <Route path="financeiro" element={<AdminFinanceiro />} />
            <Route path="health" element={<AdminHealth />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="aquisicao" element={<AdminAcquisition />} />
          </Route>
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/overview" element={<Overview />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/clients/:id" element={<ClientDetail />} />
            <Route path="/cases" element={<Cases />} />
            <Route path="/cases/:id" element={<CaseDetail />} />
            <Route path="/alerts" element={<Alerts />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/audit-log" element={<AuditLog />} />
            <Route path="/settings" element={<Settings />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
        </ErrorBoundary>
      </OfficeProvider>
      </AuthProvider>
    </BrowserRouter>
    <Analytics />
    <SpeedInsights />
    </ThemeProvider>
    </LangProvider>
  )
}
